import "katex/dist/katex.min.css";

import type { FC } from "react";
import { useState } from "react";
import Button from "react-bootstrap/Button";
import { useTranslation } from "react-i18next";
import Latex from "react-latex-next";

import { GenericBox, PrintAttachmentBox } from "components";
import { AssignmentImage } from "components/AssignmentImage";
import { LastYearResultsBanner } from "components/LastYearResultsBanner/LastYearResultsBanner";
import { FirstIncorrectAnswerModal } from "components/Modal/IncorrectAnswer/FirstIncorrectAnswer";
import { SecondIncorrectAnswerModal } from "components/Modal/IncorrectAnswer/SecondIncorrectAnswer";
import { VideoAccordion } from "features/Tutoring/shared/VideoAccordion";
import { BooleanWithTypeGuard } from "utils/types/BooleanWithTypeGuard";

import type { UserAssignmentDetailFragment } from "api/generated";

import { extractFileNameFromPath } from "utils/extractFileNameFromPath";
import { useAssetUrlResolver } from "utils/hooks/useAssetUrlResolver";

interface Props {
  userAssignment: UserAssignmentDetailFragment;
  videoTips: Array<{ videoId: string; placeholderImage?: string | null | undefined }>;
  attempt: number;
  onSubmit: () => void;
}

const Task: FC<Props> = ({ userAssignment, videoTips, attempt, onSubmit }) => {
  const assignment = userAssignment.assignment?.assignment;

  const { t } = useTranslation(["assignments"]);
  const [showModal, setShowModal] = useState(attempt > 0);
  const [activeAccordion, setAccordionActive] = useState<string | string[] | undefined | null>(null);

  const handleModalClose = () => {
    setShowModal(false);
  };
  const imageUrl = useAssetUrlResolver(assignment?.assignmentImage);
  const printAttachmentUrl = useAssetUrlResolver(assignment?.printingAttachment);

  const toggleAccordion = (state: boolean) => {
    setAccordionActive(state ? "0" : null);
  };

  const videoIds =
    userAssignment.assignment?.analysis?.hasSharedAnalysis === true
      ? [userAssignment.assignment.analysis.sharedAnalysis?.explainerVideo].filter(BooleanWithTypeGuard)
      : [
          ...new Set(
            userAssignment.assignment?.assignment?.questions
              .map(question => question.solutionAnalysis?.explainerVideo)
              .filter(BooleanWithTypeGuard),
          ),
        ];

  return (
    <div className="exercise__body" key={userAssignment.id}>
      <div className="d-flex flex-column gap-3">
        {typeof userAssignment.assignment?.successRate === "number" && (
          <LastYearResultsBanner level={userAssignment.assignment.successRate} />
        )}

        <Latex>{assignment?.assignmentText ?? ""}</Latex>
        {!!assignment?.assignmentImage && (
          <AssignmentImage
            src={imageUrl}
            alt="Zadání"
            thumbHash={assignment?.assignmentImageThumbHash}
            width={assignment?.AssignmentImageDimensions?.width}
            height={assignment?.AssignmentImageDimensions?.height}
          />
        )}

        <Latex>{assignment?.questionsText ?? ""}</Latex>

        {assignment?.questions.map((question, qIndex) => (
          <div key={qIndex}>
            <Latex>{question.heading ?? ""}</Latex>
          </div>
        ))}

        <VideoAccordion
          videoIds={videoIds}
          videoTips={videoTips}
          transcriptCs={assignment?.transcriptCs}
          transcriptEn={assignment?.transcriptEn}
          activeAccordion={activeAccordion}
          toggleAccordion={toggleAccordion}
        />
      </div>

      <div>
        {printAttachmentUrl !== null ? (
          <PrintAttachmentBox
            printAttachmentURL={printAttachmentUrl}
            pdfName={extractFileNameFromPath(assignment?.printingAttachment)}
          />
        ) : (
          <GenericBox />
        )}

        <div className="exercise__footer">
          <div className="mw-sm px-3">
            <Button className={"w-100 text-uppercase"} onClick={onSubmit}>
              {t("detail.submitSelfEvaluation", { ns: "assignments" })}
            </Button>
          </div>
        </div>
      </div>

      {showModal ? (
        <>
          {attempt === 1 || !videoIds?.length ? (
            <FirstIncorrectAnswerModal incorrectCount={1} questionsCount={1} onModalClose={handleModalClose} />
          ) : null}

          {attempt > 1 && videoIds?.length ? (
            <SecondIncorrectAnswerModal videoIds={videoIds} onModalClose={handleModalClose} attempt={attempt} />
          ) : null}
        </>
      ) : null}
    </div>
  );
};

export default Task;
